import { useEffect, useState } from 'react'
import moment from 'moment'
import { Alert, Badge, Card, Table } from 'react-bootstrap'
import { TableTitle } from '../../../../components/atoms/TableTitle'
import {
  BillingEventsResponse,
  BillingEvent
} from '../../../../domain/models/BillingModel'
import { RemoteGetBillingEvents } from '../../../../domain/usecases/BillingEvents'
import { useAxiosHttpClient } from '../../../../hooks/useAxiosHttpClient'
import { URL_API } from '../../../../services/api_url'

import * as S from './styles'

interface BillingEventsTableProps {
  billingId: string
}

export const BillingEventsTable = ({ billingId }: BillingEventsTableProps) => {
  const [eventsData, setEventsData] = useState<BillingEventsResponse | null>(
    null
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const httpClient = useAxiosHttpClient()
  const getBillingEvents = new RemoteGetBillingEvents(
    `${URL_API}/billings`,
    httpClient
  )

  useEffect(() => {
    const fetchEvents = async () => {
      if (!billingId) return

      setLoading(true)
      setError(null)

      try {
        const events = await getBillingEvents.getBillingEvents(billingId)
        setEventsData(events)
      } catch (error) {
        console.error('Error fetching billing events:', error)
        setError('Erro ao carregar eventos do recebível')
      } finally {
        setLoading(false)
      }
    }

    fetchEvents()
  }, [billingId])

  const getEventStatusBadge = (eventName: string) => {
    switch (eventName) {
      case 'RECEBIVEL_REGISTRADO':
        return <Badge variant="info">Registrado</Badge>
      case 'PAYMENT_RECEIVED':
        return <Badge variant="success">Pago</Badge>
      default:
        return <Badge variant="secondary">{eventName}</Badge>
    }
  }

  const formatEventName = (eventName: string) => {
    switch (eventName) {
      case 'RECEBIVEL_REGISTRADO':
        return 'Recebível Registrado'
      case 'PAYMENT_RECEIVED':
        return 'Pagamento Recebido'
      default:
        return eventName
    }
  }

  const getTransactionHash = (event: BillingEvent) => {
    if (event.name === 'RECEBIVEL_REGISTRADO') {
      return eventsData?.creation_transaction_hash
    } else if (event.name === 'PAYMENT_RECEIVED') {
      return eventsData?.payment_transaction_hash
    }
    return event.transaction_hash
  }

  return (
    <S.Wrapper>
      <Card className="table-card">
        <Card.Header>
          <TableTitle>Eventos do Recebível</TableTitle>
        </Card.Header>
        <Card.Body className="pb-0">
          {loading && <Alert variant="info">Carregando eventos...</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          {!loading && !error && eventsData && (
            <S.CustomTable responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>EVENTO</th>
                  <th>DATA / HORA</th>
                  <th>HASH DO EVENTO</th>
                  <th>STATUS DO EVENTO</th>
                  <th>DETALHES</th>
                </tr>
              </thead>
              <tbody>
                {eventsData.events.map((event) => (
                  <tr key={event.id}>
                    <td>{formatEventName(event.name)}</td>
                    <td>
                      {moment(event.datetime).format('DD/MM/YYYY HH:mm:ss')}
                    </td>
                    <td>
                      {getTransactionHash(event) ? (
                        <span
                          title={getTransactionHash(event)}
                          style={{ fontFamily: 'monospace', fontSize: '0.8em' }}
                        >
                          {getTransactionHash(event)?.substring(0, 20)}...
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{getEventStatusBadge(event.name)}</td>
                    <td>
                      {event.name === 'PAYMENT_RECEIVED' &&
                        event.extra_data && (
                          <div style={{ fontSize: '0.8em' }}>
                            <div>
                              <strong>Valor Pago:</strong> R${' '}
                              {Number(
                                event.extra_data.paid_amount || 0
                              ).toFixed(2)}
                            </div>
                            {Number(event.extra_data.interest_amount || 0) >
                              0 && (
                              <div>
                                <strong>Juros:</strong> R${' '}
                                {Number(
                                  event.extra_data.interest_amount || 0
                                ).toFixed(2)}
                              </div>
                            )}
                            {Number(event.extra_data.fine_amount || 0) > 0 && (
                              <div>
                                <strong>Multa:</strong> R${' '}
                                {Number(
                                  event.extra_data.fine_amount || 0
                                ).toFixed(2)}
                              </div>
                            )}
                            {Number(
                              event.extra_data.monetary_correction_amount || 0
                            ) > 0 && (
                              <div>
                                <strong>Correção:</strong> R${' '}
                                {Number(
                                  event.extra_data.monetary_correction_amount ||
                                    0
                                ).toFixed(2)}
                              </div>
                            )}
                          </div>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </S.CustomTable>
          )}
          {!loading &&
            !error &&
            eventsData &&
            eventsData.events.length === 0 && (
              <Alert variant="warning">
                Nenhum evento encontrado para este recebível.
              </Alert>
            )}
        </Card.Body>
      </Card>
    </S.Wrapper>
  )
}
